import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, filter, map, tap } from 'rxjs';

import { Gender } from '../../appointment-creation/motive-selection/model/gender';
import { RegisterInformation } from '../model/register-information';
import { StatePersistenceService } from './state-persistence.service';

@Injectable({ providedIn: 'root' })
export class EstimateConsultationService {
  private previousUrl: string | null = null;
  private currentUrl: string | null = null;
  private readonly genderSelected$ = new BehaviorSubject<Gender | null>(
    this.statePersistenceService.getPersistedStateBy('gender') as Gender | null,
  );
  private readonly registerInformation$ =
    new BehaviorSubject<RegisterInformation | null>(
      this.statePersistenceService.getPersistedStateBy(
        'info',
      ) as RegisterInformation | null,
    );
  private readonly isOnlineEstimateVerificationProcess$ =
    new BehaviorSubject<boolean>(
      this.statePersistenceService.getPersistedState(
        'isVerificationProcess',
        false,
      ) as boolean,
    );
  private readonly isOnlineEstimatePhoneVerificationProcess$ =
    new BehaviorSubject<boolean>(
      this.statePersistenceService.getPersistedState(
        'isPhoneVerificationProcess',
        false,
      ) as boolean,
    );
  readonly genderSelectedAsObservable$ = this.genderSelected$.asObservable();
  readonly registerInformationAsObservable$ =
    this.registerInformation$.asObservable();
  readonly isOnlineEstimateVerificationProcessAsObservable$ =
    this.isOnlineEstimateVerificationProcess$.asObservable();
  readonly isOnlineEstimatePhoneVerificationProcessAsObservable$ =
    this.isOnlineEstimatePhoneVerificationProcess$.asObservable();

  constructor(
    private readonly router: Router,
    private readonly statePersistenceService: StatePersistenceService,
  ) {
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        map((e) => e as NavigationEnd),

        tap((path) => {
          this.previousUrl = this.currentUrl;
          this.currentUrl = path.url;
        }),
      )
      .subscribe();
  }

  getPreviousUrl(): string | null {
    return this.previousUrl;
  }

  getGender() {
    return this.genderSelected$.value;
  }

  setGender(gender: Gender | null) {
    this.genderSelected$.next(gender);
    this.statePersistenceService.persistState('gender', gender);
  }

  saveRegisterInformation(info: RegisterInformation) {
    this.registerInformation$.next(info);
    this.statePersistenceService.persistState('info', info);
  }

  clearPatientInfo() {
    this.registerInformation$.next(null);
    this.statePersistenceService.persistState('info', null);
  }

  saveFirstAndLastName(firstName: string, lastName: string) {
    const currentInfo = this.registerInformation$.value;
    this.registerInformation$.next({
      email: currentInfo?.email || '',
      phoneNumber: currentInfo?.phoneNumber || '',
      firstName: firstName,
      lastName: lastName,
    });
  }

  getRegisterInformation() {
    return this.registerInformation$.value;
  }

  setIsVerificationProcess(isProcess: boolean) {
    this.isOnlineEstimateVerificationProcess$.next(isProcess);
  }

  getIsVerificationProcess() {
    return this.isOnlineEstimateVerificationProcess$.value;
  }

  setIsPhoneVerificationProcess(isProcess: boolean) {
    this.isOnlineEstimatePhoneVerificationProcess$.next(isProcess);
  }
}
